/**
 * AVS-4422: which car one `<avp-3dws-deck>` mount was asked to show, so a start-up echo can be
 * named in the log.
 *
 * The deck can drop a `pr-string` the host writes during its start-up and then announce the
 * AVE's older car (`update:pr-string`), the "start-up echo". The tracker keeps `held`, every car
 * the deck may hold, and a pending selection: the `target` the host wrote and the cars it
 * `replaced`. An announcement of a replaced car is an `echo`; any other is `apply`. Every
 * announcement settles: the deck holds the announced car and pending ends.
 *
 * Only start-up drops a write. Past its first car sync after `streamInitialized` the deck selects a
 * write and announces nothing, so that sync closes pending and later writes open none: a later
 * announcement of a car they replaced is another client's pick. A failure or a new
 * `streamInitialized` reopens the window.
 *
 * The caller forwards every announcement with its verdict and never writes back to the deck
 * (re-writing the target raced the deck's own re-select of the old car). Protection lives in the
 * vehicle registry, which registers a car no entry holds only for an `echo`. Pure per-mount state:
 * no DOM, no timers, no logging. A customer tab passes `guard: false` and every announcement applies.
 */
export type DeckAnnouncement = 'apply' | 'echo';

export interface IDeckSelectionPending {
  readonly replaced: ReadonlySet<string>;
  readonly target: string;
}

export interface IDeckSelection {
  /** The host rendered a new `pr-string` onto the deck. */
  written(next: string): void;
  /** The deck announced `value` via `update:pr-string`; `echo` names it for the log and the registry. */
  announced(value: string): DeckAnnouncement;
  /** The deck fired `exception` or `disconnected`. */
  failed(): void;
  /** The deck fired `streamInitialized`. */
  initialized(): void;
  /**
   * The deck's car sync ended, after any announcement it carried. Only the first after `initialized`
   * counts. Returns the pending selection it closed, which the deck kept.
   */
  synced(): IDeckSelectionPending | undefined;
  readonly pending: IDeckSelectionPending | undefined;
  readonly held: ReadonlySet<string>;
  /**
   * The cars the selection the first car sync closed had replaced, until the next announcement, write,
   * `streamInitialized` or failure. An announcement of one of them is another client's pick or a late
   * start-up echo, which would mean the deck changed its event order.
   */
  readonly acceptedAtSync: ReadonlySet<string> | undefined;
}

interface IPendingState {
  replaced: Set<string>;
  target: string;
}

export function createDeckSelection(mountedWith: string, { guard }: { guard: boolean }): IDeckSelection {
  let held = new Set([mountedWith]);
  let pending: IPendingState | undefined;
  let phase: 'starting' | 'initialized' | 'started' = 'starting';
  let acceptedAtSync: Set<string> | undefined;

  const snapshot = (state: IPendingState): IDeckSelectionPending =>
    Object.freeze({ replaced: new Set(state.replaced), target: state.target });

  return {
    written(next) {
      acceptedAtSync = undefined;
      if (!guard) return;

      if (phase === 'started') {
        held = new Set([next]);
        return;
      }

      if (pending) {
        if (next === pending.target) return;
        pending.replaced.add(pending.target);
        pending.replaced.delete(next);
        pending.target = next;
        return;
      }

      if (held.size === 1 && held.has(next)) return;
      const replaced = new Set(held);
      replaced.delete(next);
      pending = { replaced, target: next };
    },

    announced(value) {
      // Pending only ever opens with the guard on, so a customer tab always applies.
      const echo = pending?.replaced.has(value) === true;
      held = new Set([value]);
      pending = undefined;
      acceptedAtSync = undefined;
      return echo ? 'echo' : 'apply';
    },

    failed() {
      phase = 'starting';
      acceptedAtSync = undefined;
      if (!guard || !pending) return;
      held = new Set([...pending.replaced, pending.target]);
      pending = undefined;
    },

    initialized() {
      phase = 'initialized';
      acceptedAtSync = undefined;
    },

    synced() {
      if (phase !== 'initialized') return undefined;
      phase = 'started';
      if (!pending) return undefined;
      const closed = snapshot(pending);
      held = new Set([pending.target]);
      acceptedAtSync = new Set(pending.replaced);
      pending = undefined;
      return closed;
    },

    get pending() {
      return pending ? snapshot(pending) : undefined;
    },

    get held() {
      return new Set(held);
    },

    get acceptedAtSync() {
      return acceptedAtSync ? new Set(acceptedAtSync) : undefined;
    },
  };
}
